// Ponte verso il guscio nativo (heyjob-app, Expo WebView).
//
// Il webapp gira in due modi: dentro il guscio nativo, oppure come PWA/sito nel
// browser. Qui c'è solo il canale di comunicazione: chi chiama deve sempre
// prevedere il fallback web (vedi utils/geolocation.js).
//
// Contratto: heyjob-app/src/webview/bridge.ts (fonte unica dei tipi).

const NATIVE_EVENT = 'heyjobNativeMessage';

/** true se stiamo girando dentro il guscio nativo. */
export function isNativeApp() {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
}

/** Info iniettate dal guscio: { platform, appVersion, bridgeVersion }. */
export function nativeInfo() {
  if (typeof window === 'undefined') return null;
  return window.__HEYJOB_NATIVE__ ?? null;
}

/** Invia un messaggio al guscio. Ritorna false se non siamo nel nativo. */
export function sendToNative(message) {
  if (!isNativeApp()) return false;
  window.ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

/** Ascolta i messaggi dal guscio. Ritorna la funzione di unsubscribe. */
export function onNativeMessage(handler) {
  const listener = (event) => handler(event.detail);
  window.addEventListener(NATIVE_EVENT, listener);
  return () => window.removeEventListener(NATIVE_EVENT, listener);
}

let sequence = 0;

function nextRequestId() {
  sequence += 1;
  return `hj-${Date.now()}-${sequence}`;
}

/**
 * Richiesta una-tantum al guscio, correlata via requestId.
 * Rifiuta se non siamo nel nativo o se il guscio non risponde entro `timeout`
 * (senza timeout una promise appesa bloccherebbe la UI che la attende).
 */
export function requestFromNative(message, { timeout = 15000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!isNativeApp()) {
      reject(new Error('native bridge non disponibile'));
      return;
    }

    const requestId = nextRequestId();
    let timer;

    const off = onNativeMessage((detail) => {
      if (detail?.requestId !== requestId) return;
      clearTimeout(timer);
      off();
      resolve(detail);
    });

    timer = setTimeout(() => {
      off();
      reject(new Error('native bridge timeout'));
    }, timeout);

    sendToNative({ ...message, requestId });
  });
}

/**
 * Sottoscrive uno stream di messaggi correlati (es. watch della posizione):
 * invia `startMessage`, instrada al handler ogni messaggio con lo stesso
 * requestId, e alla pulizia invia `stopMessage`.
 * Ritorna la funzione di stop (no-op se non siamo nel nativo).
 */
export function streamFromNative(startMessage, stopMessage, handler) {
  if (!isNativeApp()) return () => {};

  const requestId = nextRequestId();

  const off = onNativeMessage((detail) => {
    if (detail?.requestId === requestId) handler(detail);
  });

  sendToNative({ ...startMessage, requestId });

  return () => {
    off();
    sendToNative({ ...stopMessage, requestId });
  };
}
